import React from 'react';
import type { Period } from '../types';
import { D1, FONT, d1 } from '../design/tokens';
import { GeoLogo } from '../design/GeoLogo';

export type TeacherTab = "inicio" | "chamada" | "relatorios" | "alunos" | "periodos";

const NAV: [TeacherTab, string][] = [
  ["inicio", "Painel"], ["chamada", "Chamada"], ["relatorios", "Relatórios"], ["alunos", "Alunos"], ["periodos", "Períodos"],
];

// Largura da janela (para o responsivo: <1100 menu vira faixa, <700 compacta)
export function useViewportWidth() {
  const [w, setW] = React.useState<number>(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  React.useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

// PIN flutuante: fica visível em qualquer aba enquanto houver chamada aberta,
// para a turma ver o PIN mesmo quando a professora está em Relatórios etc.
export function FloatingPin({ items, onOpen }: {
  items: { turma: string; pin: string; registros: number; total: number }[];
  onOpen: () => void;
}) {
  const [min, setMin] = React.useState(false);
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => { const iv = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(iv); }, []);
  if (!items.length) return null;
  const secs = 30 - Math.floor((now % 30000) / 1000);
  const fmt = (p: string) => (p && p.length === 6 ? p.slice(0, 3) + ' ' + p.slice(3) : p);
  return (
    <div className="gf-noprint" style={{ position: "fixed", right: 20, bottom: 20, zIndex: 900, background: "#fff", border: `1.5px solid ${D1.ok}`, boxShadow: "0 6px 24px rgba(28,36,48,.18)", fontFamily: FONT.sans, minWidth: min ? 0 : 260 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: D1.okSoft, borderBottom: min ? "none" : `1px solid ${D1.line}` }}>
        <span style={{ ...d1.cap, fontSize: 10, color: D1.ok }}>● Chamada aberta</span>
        {min && items.map(it => <span key={it.turma} style={{ fontFamily: FONT.mono, fontWeight: 600, fontSize: 15 }}>{it.turma} {fmt(it.pin)}</span>)}
        <button onClick={() => setMin(m => !m)} title={min ? "Expandir" : "Minimizar"} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: D1.ink2, fontSize: 14, padding: 0 }}>{min ? "▢" : "–"}</button>
      </div>
      {!min && (
        <div style={{ padding: "10px 14px 12px" }}>
          {items.map((it, i) => (
            <div key={it.turma} style={{ paddingTop: i ? 10 : 0, marginTop: i ? 10 : 0, borderTop: i ? `1px solid ${D1.line2}` : "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={{ ...d1.cap, fontSize: 10 }}>PIN · Turma {it.turma}</span>
                <span style={{ fontSize: 12, color: D1.ink2 }}><b style={{ fontFamily: FONT.mono, color: D1.ink }}>{it.registros}</b> / {it.total} entraram</span>
              </div>
              <div style={{ fontFamily: FONT.mono, fontSize: 38, fontWeight: 600, letterSpacing: 2, lineHeight: 1.15, whiteSpace: "nowrap" }}>{fmt(it.pin)}</div>
            </div>
          ))}
          <div style={{ height: 3, background: D1.line2, marginTop: 8 }}><div style={{ height: "100%", width: `${((30 - secs) / 30) * 100}%`, background: D1.acc }} /></div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, fontSize: 12, color: D1.ink2 }}>
            <span>troca em <b style={{ fontFamily: FONT.mono }}>00:{String(secs).padStart(2, "0")}</b></span>
            <button onClick={onOpen} style={{ ...d1.link, fontSize: 12 }}>Abrir Chamada →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Layout comum das telas da professora: faixa institucional, barra de marca
// com o seletor de período, menu lateral e área de conteúdo.
export function TeacherShell({ tab, onNav, onLogout, periods, viewPeriodId, setViewPeriodId, isViewingActive, extraTopRight, children }: {
  tab: TeacherTab; onNav: (t: TeacherTab) => void; onLogout: () => void;
  periods: Period[]; viewPeriodId: string; setViewPeriodId: (id: string) => void; isViewingActive: boolean;
  extraTopRight?: React.ReactNode; children: React.ReactNode;
}) {
  const w = useViewportWidth();
  const narrow = w < 1100;
  const tiny = w < 700;
  return (
    <div className="gf-teacher" style={{ minHeight: "100vh", background: D1.paper, fontFamily: FONT.sans, color: D1.ink, display: "flex", flexDirection: "column" }}>
      {/* 3.1 Faixa superior */}
      <div className="gf-noprint" style={{ background: D1.acc, color: "#fff", fontSize: 11, letterSpacing: 0.6, padding: tiny ? "6px 16px" : "6px 32px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <span>GEO Frequência</span>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {extraTopRight}
          <span>Profª Laura Valencia · <button onClick={onLogout} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", font: "inherit", letterSpacing: "inherit", padding: 0, textDecoration: "underline" }}>Sair</button></span>
        </span>
      </div>

      {/* 3.2 Barra de marca */}
      <div className="gf-noprint" style={{ display: "flex", alignItems: "center", gap: 14, padding: tiny ? "12px 16px" : "16px 32px", borderBottom: `1px solid ${D1.line}`, background: "#fff", flexWrap: "wrap" }}>
        <GeoLogo size={38} />
        <div>
          <div style={{ fontFamily: FONT.serif, fontSize: 21, fontWeight: 600, lineHeight: 1.2 }}>GEO Frequência</div>
          <div style={{ ...d1.cap, fontSize: 10, marginTop: 1 }}>Registro de frequência discente</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: D1.ink2 }}>
          <span>Período</span>
          <select value={viewPeriodId} onChange={e => setViewPeriodId(e.target.value)}
            style={{ fontFamily: FONT.mono, fontWeight: 600, fontSize: 13, color: D1.ink, border: `1px solid ${D1.line}`, padding: "5px 10px", borderRadius: 3, background: "#fff", cursor: "pointer" }}>
            {periods.length === 0 && <option value="">—</option>}
            {periods.map(p => <option key={p.id} value={p.id}>{p.label}{p.active ? "" : " (histórico)"}</option>)}
          </select>
          {!isViewingActive && periods.length > 0 && <span style={{ ...d1.cap, fontSize: 10, color: "#7a5a12", background: "#faf4e1", padding: "3px 8px", borderRadius: 2 }}>Histórico</span>}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: narrow ? "column" : "row", flex: 1, minHeight: 0 }}>
        {/* 3.3 Menu lateral (ou faixa horizontal em telas estreitas) */}
        {narrow ? (
          <nav className="gf-noprint" style={{ display: "flex", background: "#fff", borderBottom: `1px solid ${D1.line}`, overflowX: "auto", padding: tiny ? "0 8px" : "0 24px" }}>
            {NAV.map(([k, label]) => {
              const on = tab === k;
              return <button key={k} onClick={() => onNav(k)} style={{ padding: "12px 16px", fontSize: 14, fontFamily: FONT.sans, fontWeight: on ? 600 : 400, color: on ? D1.acc : D1.ink2, background: "none", border: "none", borderBottom: `3px solid ${on ? D1.acc : "transparent"}`, cursor: "pointer", whiteSpace: "nowrap" }}>{label}</button>;
            })}
          </nav>
        ) : (
          <nav className="gf-noprint" style={{ width: 210, flexShrink: 0, borderRight: `1px solid ${D1.line}`, padding: "22px 0", background: "#fff" }}>
            <div style={{ ...d1.cap, padding: "0 24px 10px" }}>Docente</div>
            {NAV.map(([k, label]) => {
              const on = tab === k;
              return <button key={k} onClick={() => onNav(k)} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 24px", fontSize: 14, fontFamily: FONT.sans, fontWeight: on ? 600 : 400, color: on ? D1.acc : D1.ink2, background: on ? D1.accSoft : "transparent", borderTop: "none", borderRight: "none", borderBottom: "none", borderLeftStyle: "solid", borderLeftWidth: 3, borderLeftColor: on ? D1.acc : "transparent", cursor: "pointer" }}>{label}</button>;
            })}
          </nav>
        )}

        {/* 3.4 Área de conteúdo */}
        <main style={{ flex: 1, minWidth: 0, padding: tiny ? 16 : "28px 36px", overflowX: "auto", boxSizing: "border-box" }}>
          {children}
        </main>
      </div>
    </div>
  );
}
